import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { EmptyState } from "@/shared/components/EmptyState";
import { Spinner } from "@/shared/components/spinner";
import { getUploadStatus } from "@/features/patient/records/api/dataPipelineApi";
import { UploadHistoryList, type ZipUploadEntry } from "@/features/patient/records/components/UploadHistoryList";
import { UploadDetailPanel } from "@/features/patient/records/components/UploadDetailPanel";
import { useUploadHistory } from "@/portals/patient/context/UploadHistoryContext";

// The left rail's width is drag-adjustable (desktop only) via the divider
// between it and the data panel — drag left to give the data panel more room,
// which squeezes the upload-name list. Clamped so neither side collapses, and
// remembered per browser (a lightweight view preference — safe in
// localStorage, falls back cleanly when it's unavailable).
const SIDEBAR_MIN_PX = 200;
const SIDEBAR_MAX_PX = 560;
// The data panel never shrinks below this, regardless of drag — the real max
// sidebar width is min(SIDEBAR_MAX_PX, containerWidth - DATA_PANEL_MIN_PX).
const DATA_PANEL_MIN_PX = 420;
const SIDEBAR_DEFAULT_PX = 320;
const SIDEBAR_WIDTH_STORAGE_KEY = "medsync:uploadHistory:sidebarWidthPx";
const SIDEBAR_KEYBOARD_STEP_PX = 24;

function clampSidebar(px: number, containerWidth?: number): number {
  const upper = containerWidth
    ? Math.max(SIDEBAR_MIN_PX, Math.min(SIDEBAR_MAX_PX, containerWidth - DATA_PANEL_MIN_PX))
    : SIDEBAR_MAX_PX;
  return Math.round(Math.min(upper, Math.max(SIDEBAR_MIN_PX, px)));
}

function readStoredSidebarWidth(): number {
  try {
    const raw = window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    const parsed = raw ? Number.parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) ? clampSidebar(parsed) : SIDEBAR_DEFAULT_PX;
  } catch {
    return SIDEBAR_DEFAULT_PX;
  }
}

/** "Document Panel" — left rail lists every upload (month-grouped, paginated),
 *  right panel shows whichever one is selected: its files, parsing issues, and
 *  parsed FHIR records. Reached from the sidebar, the "View Recent Uploads"
 *  button, and any recent-upload row on the Upload Records page. */
export function UploadHistoryView({ actingAsProfileId }: { actingAsProfileId?: string }) {
  const {
    zipUploads, isLoading, updateZipEntry,
    hasMoreUploads, isLoadingMoreUploads, loadMoreUploads,
  } = useUploadHistory();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isFetchingContents, setIsFetchingContents] = useState(false);

  const gridRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const [sidebarWidth, setSidebarWidth] = useState<number>(readStoredSidebarWidth);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(sidebarWidth));
    } catch {
      // Private mode / storage disabled — the width just won't persist.
    }
  }, [sidebarWidth]);

  // Keep the width valid when the window (and so the grid) is resized — an
  // earlier drag to 520px shouldn't survive shrinking the browser to where
  // that leaves the data panel too narrow.
  useEffect(() => {
    const onResize = () => {
      const width = gridRef.current?.getBoundingClientRect().width;
      if (width) setSidebarWidth((w) => clampSidebar(w, width));
    };
    onResize(); // also re-clamp a stored width against the current viewport on mount
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const applyDragX = useCallback((clientX: number) => {
    const rect = gridRef.current?.getBoundingClientRect();
    if (!rect) return;
    setSidebarWidth(clampSidebar(clientX - rect.left, rect.width));
  }, []);

  const onDividerPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  };
  const onDividerPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (draggingRef.current) applyDragX(e.clientX);
  };
  const endDividerDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  };
  const onDividerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const delta = e.key === "ArrowLeft" ? -SIDEBAR_KEYBOARD_STEP_PX : e.key === "ArrowRight" ? SIDEBAR_KEYBOARD_STEP_PX : 0;
    if (!delta) return;
    e.preventDefault();
    const width = gridRef.current?.getBoundingClientRect().width;
    setSidebarWidth((w) => clampSidebar(w + delta, width));
  };

  // Picks an initial selection once history loads: the ?uploadId= deep link if
  // it names a completed OR still-processing upload (a link into an in-progress
  // upload — e.g. this page reloaded while one was running — should still land
  // on it, not fall through to some unrelated completed upload), else simply
  // the newest upload overall — zipUploads is always newest-first (the server
  // returns it that way, and a freshly-submitted upload is prepended to the
  // front the same way), so this is "whatever the patient most recently did,"
  // period, regardless of its status.
  //
  // This used to instead search for "the most recent COMPLETED upload," which
  // was a real bug: UploadRecords navigates here with no ?uploadId= at all
  // right after the patient presses Upload, specifically so they land on that
  // upload to watch it process live — but for anyone with existing upload
  // history (i.e. almost everyone past their first upload), that search would
  // always find some older, unrelated completed upload before ever reaching
  // the brand-new "processing" one at the front of the list, silently
  // stranding the patient on the wrong upload's data.
  // Re-runs whenever the current selection stops existing in `zipUploads` (not
  // just once on first load) — belt-and-suspenders against any future version
  // of the race this used to have: UploadHistoryContext's hydration used to
  // unconditionally replace `zipUploads` with the server's list, silently
  // discarding a just-submitted upload's optimistic entry if that fetch was
  // still in flight when it was added — orphaning whatever was selected with
  // no way to recover. That's fixed at the source now (the context merges
  // instead of replacing), but auto-healing here too means a stale selection
  // never just sits on an empty "Select an upload" state indefinitely.
  // Tracks the last ?uploadId= this effect actually applied. Needed because
  // navigating to this same route with a NEW ?uploadId= (e.g. landing back
  // from an Epic connect, or submitting a fresh upload while already on this
  // page) doesn't remount this component — selectedId just keeps whatever it
  // was. Without this, the guard below ("already have a valid selection?
  // stop") short-circuits on the stale selection and the new deep link is
  // silently ignored — the exact bug this fixes: landing from Epic connect
  // while already viewing an older upload stayed stuck on that older one.
  const appliedUploadIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (zipUploads.length === 0) return;
    const requested = searchParams.get("uploadId");

    if (requested && requested !== appliedUploadIdRef.current) {
      const requestedEntry = zipUploads.find(
        (e) => e.id === requested && (e.status === "complete" || e.status === "processing"),
      );
      if (requestedEntry) {
        appliedUploadIdRef.current = requested;
        setSelectedId(requestedEntry.id);
        return;
      }
      // Not in zipUploads yet (e.g. the optimistic entry hasn't landed on
      // this exact render) — fall through and retry once zipUploads updates.
    }

    if (selectedId && zipUploads.some((e) => e.id === selectedId)) return;
    const target = zipUploads[0];
    if (target) setSelectedId(target.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipUploads, selectedId, searchParams]);

  const selectedEntry = zipUploads.find((e) => e.id === selectedId) ?? null;

  // Lazily fetches file/issue contents for an upload row that hasn't had them
  // fetched yet (e.g. history loaded from listUploads, which doesn't include
  // them) — same fetch getUploadStatus/openContentsModal used to do, just
  // triggered by selection here instead of a click handler in the parent.
  useEffect(() => {
    if (!selectedEntry?.uploadId || selectedEntry.files || selectedEntry.parsingIssues) return;
    let cancelled = false;
    setIsFetchingContents(true);
    getUploadStatus(selectedEntry.uploadId, actingAsProfileId)
      .then((result) => {
        if (cancelled) return;
        updateZipEntry(selectedEntry.id, { files: result.files ?? [], parsingIssues: result.parsing_issues ?? [] });
      })
      .catch(() => {
        // Leave the row as-is — UploadDetailPanel still works with an empty files/issues list.
      })
      .finally(() => { if (!cancelled) setIsFetchingContents(false); });
    return () => { cancelled = true; };
  }, [selectedEntry, actingAsProfileId, updateZipEntry]);

  const handleSelect = (entry: ZipUploadEntry) => {
    setSelectedId(entry.id);
    const next = new URLSearchParams(searchParams);
    next.set("uploadId", entry.id);
    setSearchParams(next, { replace: true });
  };

  // Only the Issues tab's own content (and the Files Found grid) actually
  // depends on this fetch — the header, the tabs, and the Parsed Data/View
  // PDF Data tabs' own independent fetches don't need it at all. Blanking
  // the WHOLE right panel to a bare "Loading…" for this one fetch used to
  // hide all of that too; UploadDetailPanel now renders immediately and
  // gets isLoadingContents to show a small in-place indicator only where
  // it's actually relevant.
  const isLoadingSelectedContents = isFetchingContents && !selectedEntry?.files && !selectedEntry?.parsingIssues;

  return (
    <div className="flex flex-col gap-5 p-4 lg:h-full lg:p-6">
      <div
        ref={gridRef}
        className="relative grid grid-cols-1 gap-5 lg:min-h-0 lg:flex-1 lg:grid-cols-[var(--sidebar-w)_1fr]"
        style={{ "--sidebar-w": `${sidebarWidth}px` } as React.CSSProperties}
      >
        <div className="min-w-0 lg:h-full lg:min-h-0">
          <UploadHistoryList
            entries={zipUploads}
            selectedId={selectedId}
            onSelect={handleSelect}
            isLoading={isLoading}
            hasMore={hasMoreUploads}
            isLoadingMore={isLoadingMoreUploads}
            onLoadMore={loadMoreUploads}
          />
        </div>

        {/* Drag divider — sits in the gap between the rail and the data
            panel, desktop only. Drag left → data panel grows, upload list
            shrinks. */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize upload list"
          aria-valuemin={SIDEBAR_MIN_PX}
          aria-valuemax={SIDEBAR_MAX_PX}
          aria-valuenow={Math.round(sidebarWidth)}
          tabIndex={0}
          onPointerDown={onDividerPointerDown}
          onPointerMove={onDividerPointerMove}
          onPointerUp={endDividerDrag}
          onPointerCancel={endDividerDrag}
          onKeyDown={onDividerKeyDown}
          className="group absolute inset-y-0 z-10 hidden w-4 -translate-x-1/2 cursor-col-resize touch-none items-center justify-center outline-none lg:flex"
          style={{ left: "calc(var(--sidebar-w) + 10px)" }}
        >
          <div className="h-12 w-1 rounded-full bg-[#D1D5DB] transition-colors group-hover:bg-[#F17893] group-focus-visible:bg-[#F17893] group-active:bg-[#F17893]" />
        </div>

        <div className="min-h-[400px] min-w-0 rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm lg:h-full lg:min-h-0">
          {isLoading && zipUploads.length === 0 ? (
            // Only while genuinely nothing is known yet — same reasoning as
            // UploadHistoryList's own isLoading guard: an entry (and
            // therefore a selectedEntry) can already exist during this
            // fetch (e.g. useEpicConnectReturn's optimistic "Epic Sync"
            // row), and blindly spinning here used to hide it for a second
            // or two for no reason.
            <div className="flex h-full items-center justify-center text-sm text-[#8A8A8A]">
              <Spinner />
            </div>
          ) : !selectedEntry ? (
            <EmptyState
              icon={<Icon icon="lucide:folder-open" className="h-8 w-8 text-[#9CA3AF]" />}
              title={zipUploads.length === 0 ? "No uploads yet" : "Select an upload"}
              description={zipUploads.length === 0 ? "Files you upload will show up here." : "Choose an upload on the left to see what we found in it."}
              className="h-full justify-center border-none shadow-none"
            />
          ) : (
            <UploadDetailPanel
              entry={selectedEntry}
              actingAsProfileId={actingAsProfileId}
              isLoadingContents={isLoadingSelectedContents}
            />
          )}
        </div>
      </div>
    </div>
  );
}
